import { deriveBrandScale, ensureReadablePair } from './color'

export interface Branding {
  appName: string
  shortName: string
  logoUrl: string | null
  icon192Url: string | null
  icon512Url: string | null
  faviconUrl: string | null
  coverUrl: string | null
  primaryColor: string
  secondaryColor: string
  accentColor: string
  backgroundColor: string
  textColor: string
  darkMode: boolean
  version: string
}

export type ThemeMode = 'light' | 'dark'

/** O painel usa a marca com parcimônia; o portal, por inteiro. */
export type ThemeIntensity = 'panel' | 'full'

export const DEFAULT_BRANDING: Branding = {
  appName: 'SteinGym',
  shortName: 'SteinGym',
  logoUrl: null,
  icon192Url: null,
  icon512Url: null,
  faviconUrl: null,
  coverUrl: null,
  primaryColor: '#2B59FF',
  secondaryColor: '#0C1116',
  accentColor: '#00C2A8',
  backgroundColor: '#FBFCFD',
  textColor: '#161C21',
  darkMode: false,
  version: '0',
}

const THEME_CACHE_PREFIX = 'steingym.theme'

export function themeCacheKey(slug?: string): string {
  return slug ? `${THEME_CACHE_PREFIX}.${slug}` : THEME_CACHE_PREFIX
}

/**
 * Escreve as CSS variables e o theme-color. É o único ponto do app que
 * define cor de marca — nenhum componente escreve hex.
 */
export function applyTheme(
  branding: Branding,
  mode: ThemeMode,
  intensity: ThemeIntensity = 'full',
  root: HTMLElement = document.documentElement,
): void {
  const scale = deriveBrandScale(branding.primaryColor, mode)
  const accent = deriveBrandScale(branding.accentColor, mode)

  root.dataset.theme = mode
  root.dataset.brandIntensity = intensity

  const vars: Record<string, string> = {
    '--brand': scale.brand,
    '--brand-fg': scale.brandFg,
    '--brand-text': scale.brandText,
    '--brand-hover': scale.brandHover,
    '--brand-active': scale.brandActive,
    '--brand-subtle': scale.brandSubtle,
    '--brand-subtle-fg': scale.brandSubtleFg,
    '--brand-ring': scale.brandRing,
    '--accent': accent.brand,
    '--accent-fg': accent.brandFg,
    '--accent-subtle': accent.brandSubtle,
  }

  // O personal informa fundo e texto como preferência; se o par não
  // atingir 4.5:1, mantemos o matiz e ajustamos a luminosidade.
  if (intensity === 'full' && mode === 'light') {
    const pair = ensureReadablePair(branding.backgroundColor, branding.textColor)
    vars['--bg'] = pair.background
    vars['--fg'] = pair.text
  }

  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value)
  }

  setMeta('theme-color', mode === 'dark' ? '#0C1116' : scale.brand)
}

function setMeta(name: string, content: string): void {
  if (typeof document === 'undefined') return
  let tag = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.name = name
    document.head.appendChild(tag)
  }
  tag.content = content
}

/** Aponta o manifest e o favicon para os do personal. */
export function applyAppIdentity(branding: Branding, slug: string, apiUrl: string): void {
  if (typeof document === 'undefined') return

  document.title = branding.appName

  setLink('manifest', `${apiUrl}/public/personals/${slug}/manifest.webmanifest`)

  if (branding.faviconUrl) {
    setLink('icon', `${branding.faviconUrl}?v=${branding.version}`)
  }
  if (branding.icon192Url) {
    setLink('apple-touch-icon', `${branding.icon192Url}?v=${branding.version}`)
  }

  setMeta('apple-mobile-web-app-title', branding.shortName)
  // `apple-mobile-web-app-capable` está deprecado; o padrão é este.
  setMeta('mobile-web-app-capable', 'yes')
}

function setLink(rel: string, href: string): void {
  let tag = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!tag) {
    tag = document.createElement('link')
    tag.rel = rel
    document.head.appendChild(tag)
  }
  tag.href = href
}

// ── Cache local da marca ──────────────────────────────────────

/**
 * Sem isto, o aluno vê um lampejo azul-padrão antes da marca do personal
 * aparecer — exatamente o que destrói a ilusão de "app do meu personal".
 */
export function cacheBranding(branding: Branding, slug?: string): void {
  try {
    localStorage.setItem(themeCacheKey(slug), JSON.stringify(branding))
  } catch {
    // localStorage pode falhar em janela privada; o tema só perde o cache.
  }
}

export function readCachedBranding(slug?: string): Branding | null {
  try {
    const raw = localStorage.getItem(themeCacheKey(slug))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Branding
    return typeof parsed.primaryColor === 'string' ? parsed : null
  } catch {
    return null
  }
}

/**
 * Os aplicativos que ESTE aparelho já abriu, lidos do cache de marca.
 *
 * Existe para devolver o aluno que se perdeu: o portal vive em
 * `/app/:slug`, e quem digita só o domínio cai no login do PAINEL, onde a
 * senha correta dele sempre falha — de propósito, porque uma mensagem
 * específica seria um oráculo de enumeração de contas (spec 02 §2). Sem o
 * link do convite à mão, ele ficava preso com a senha certa.
 *
 * Não vaza nada: é o cache do próprio navegador de quem está olhando, e só
 * lista marcas que já passaram por ali.
 */
export function knownTenants(): { slug: string; appName: string; logoUrl: string | null }[] {
  const found: { slug: string; appName: string; logoUrl: string | null }[] = []

  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i)
      // `steingym.theme.<slug>`; o do painel não tem sufixo e é ignorado.
      if (!key?.startsWith(`${THEME_CACHE_PREFIX}.`)) continue

      const slug = key.slice(THEME_CACHE_PREFIX.length + 1)
      if (!slug) continue

      const branding = readCachedBranding(slug)
      if (branding?.appName) {
        found.push({ slug, appName: branding.appName, logoUrl: branding.logoUrl ?? null })
      }
    }
  } catch {
    // Janela privada ou armazenamento bloqueado: sem memória, sem atalho.
  }

  return found
}

const MODE_KEY = 'steingym.mode'

export function readPreferredMode(slug: string | undefined, fallback: ThemeMode): ThemeMode {
  try {
    const stored = localStorage.getItem(`${MODE_KEY}${slug ? `.${slug}` : ''}`)
    return stored === 'dark' || stored === 'light' ? stored : fallback
  } catch {
    return fallback
  }
}

export function storePreferredMode(mode: ThemeMode, slug?: string): void {
  try {
    localStorage.setItem(`${MODE_KEY}${slug ? `.${slug}` : ''}`, mode)
  } catch {
    // sem persistência de preferência; o app continua funcionando
  }
}
